import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV } from "../data";
import { Logo } from "./Logo";
import { useAppraisal } from "./AppraisalContext";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const { openDialog } = useAppraisal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-kept-sand/85 border-b border-kept-ink/8 py-4" : "py-6 bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[0.8rem] tracking-wide link-underline transition-colors ${
                pathname === item.to ? "brass-text font-semibold" : "text-kept-ink/70 hover:text-kept-ink"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={openDialog}
            className="hidden md:inline-flex group items-center gap-2 bg-kept-ink text-kept-brass px-6 py-3 kept-label"
            data-testid="header-book-appraisal"
          >
            Book Appraisal
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            className="lg:hidden text-kept-ink"
            onClick={() => setMenuOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-kept-ink z-50 flex flex-col p-8 grain"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center">
              <Logo light />
              <button onClick={() => setMenuOpen(false)} className="text-kept-sand" data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-col gap-2 mt-16">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ x: 30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={item.to}
                    className="font-serif text-4xl text-kept-sand py-2 block"
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <button
              onClick={() => { setMenuOpen(false); openDialog(); }}
              className="mt-auto bg-kept-brass text-kept-ink py-4 kept-label"
              data-testid="mobile-book-appraisal"
            >
              Book Your Free Rental Appraisal
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
